import { useMemo, useState } from "react";
import axios from "axios";
import IsLoading from "../components/IsLoading";
import { API_KEYS, API_URLS, CHAIN_IDS } from "../config/api";

const NETWORKS = [
  { label: "Ethereum", value: "ETH", native: "ETH" },
  { label: "BSC", value: "BSC", native: "BNB" },
  { label: "Polygon", value: "MATIC", native: "MATIC" },
  { label: "Avalanche", value: "AVAX", native: "AVAX" },
  { label: "Arbitrum", value: "ARB", native: "ETH" },
  { label: "Base", value: "BASE", native: "ETH" },
  { label: "Fantom", value: "FTM", native: "FTM" },
];

const PAGE_SIZE = 10000;
const MAX_PAGES = 10;

const isAddress = (value) => /^0x[a-fA-F0-9]{40}$/.test(value.trim());

const buildEtherscanUrl = (network, params) => {
  const searchParams = new URLSearchParams({
    chainid: String(CHAIN_IDS[network]),
    apikey: API_KEYS[network] || "",
    ...params,
  });

  return `${API_URLS[network]}?${searchParams.toString()}`;
};

const formatTokenAmount = (rawBalance, decimals) => {
  if (rawBalance === 0n) return "0";

  const divisor = 10n ** window.BigInt(decimals);
  const whole = rawBalance / divisor;
  const fraction = rawBalance % divisor;

  if (fraction === 0n) return whole.toString();

  const trimmedFraction = fraction
    .toString()
    .padStart(decimals, "0")
    .slice(0, 6)
    .replace(/0+$/, "");

  if (!trimmedFraction) return `${whole.toString()} (<0.000001)`;

  return `${whole.toString()}.${trimmedFraction}`;
};

const parseTokenTransfers = (transfers, address) => {
  const addressLower = address.toLowerCase();
  const balancesByToken = {};

  transfers.forEach((tx) => {
    const contractAddress = tx.contractAddress?.toLowerCase();
    const value = window.BigInt(tx.value || "0");

    if (!contractAddress || value === 0n) return;

    if (!balancesByToken[contractAddress]) {
      const decimals = Number(tx.tokenDecimal || 18);

      balancesByToken[contractAddress] = {
        contractAddress,
        name: tx.tokenName || "Unknown token",
        symbol: tx.tokenSymbol || "UNKNOWN",
        decimals: Number.isFinite(decimals) ? decimals : 18,
        rawBalance: 0n,
      };
    }

    if (tx.to?.toLowerCase() === addressLower) {
      balancesByToken[contractAddress].rawBalance += value;
    }

    if (tx.from?.toLowerCase() === addressLower) {
      balancesByToken[contractAddress].rawBalance -= value;
    }
  });

  return Object.values(balancesByToken)
    .filter((token) => token.rawBalance > 0n)
    .map((token) => ({
      ...token,
      balance: formatTokenAmount(token.rawBalance, token.decimals),
    }))
    .sort((a, b) => a.symbol.localeCompare(b.symbol));
};

export default function TimeMachine() {
  const [address, setAddress] = useState("");
  const [network, setNetwork] = useState("ETH");
  const [date, setDate] = useState("");
  const [blockNumber, setBlockNumber] = useState("");
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isTruncated, setIsTruncated] = useState(false);

  const selectedNetwork = useMemo(
    () => NETWORKS.find((item) => item.value === network),
    [network]
  );

  const fetchBlockNumber = async (timestamp) => {
    const url = buildEtherscanUrl(network, {
      module: "block",
      action: "getblocknobytime",
      timestamp: String(timestamp),
      closest: "before",
    });
    const { data } = await axios.get(url);

    if (data.status !== "1") {
      throw new Error(data.result || data.message || "Unable to find block");
    }

    return data.result;
  };

  const fetchTokenTransfers = async (endBlock) => {
    let page = 1;
    let transfers = [];
    let hasMore = true;

    while (hasMore && page <= MAX_PAGES) {
      const url = buildEtherscanUrl(network, {
        module: "account",
        action: "tokentx",
        address: address.trim(),
        startblock: "0",
        endblock: String(endBlock),
        page: String(page),
        offset: String(PAGE_SIZE),
        sort: "asc",
      });
      const { data } = await axios.get(url);

      if (data.status === "0" && data.message === "No transactions found") {
        return transfers;
      }

      if (!Array.isArray(data.result)) {
        throw new Error(data.result || data.message || "Unable to fetch transfers");
      }

      transfers = transfers.concat(data.result);
      hasMore = data.result.length === PAGE_SIZE;
      page += 1;
    }

    setIsTruncated(hasMore);
    return transfers;
  };

  const scanHoldings = async (event) => {
    event.preventDefault();
    setError("");
    setHoldings([]);
    setBlockNumber("");
    setIsTruncated(false);

    if (!API_KEYS[network]) {
      setError(`Missing API key for ${selectedNetwork.label}`);
      return;
    }

    if (!isAddress(address)) {
      setError("Invalid wallet address");
      return;
    }

    if (!date) {
      setError("Date is required");
      return;
    }

    try {
      setLoading(true);
      const timestamp = Math.floor(new Date(`${date}T23:59:59`).getTime() / 1000);
      const endBlock = await fetchBlockNumber(timestamp);
      const transfers = await fetchTokenTransfers(endBlock);

      setBlockNumber(endBlock);
      setHoldings(parseTokenTransfers(transfers, address));
    } catch (err) {
      setError(err.message || "Unable to scan wallet");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <form
        onSubmit={scanHoldings}
        className="flex flex-col w-11/12 max-w-[520px] p-4 m-auto space-y-4 border-2 border-gray-200 rounded-md"
      >
        <h1 className="text-xl font-bold text-center">Time Machine</h1>

        <div>
          <label className="label-1">Wallet Address</label>
          <input
            type="text"
            placeholder="0x..."
            value={address}
            onChange={(event) => setAddress(event.target.value)}
            className="input-1"
          />
        </div>

        <div>
          <label className="label-1">Network</label>
          <select
            value={network}
            onChange={(event) => setNetwork(event.target.value)}
            className="input-1"
          >
            {NETWORKS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label-1">Date</label>
          <input
            type="date"
            value={date}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(event) => setDate(event.target.value)}
            className="input-1"
          />
        </div>

        <button
          type="submit"
          className="px-10 py-3.5 w-full text-center rounded-md shadow-md block sm:w-auto cursor-pointer duration-300 bg-indigo-600 text-white"
        >
          Scan Holdings
        </button>
      </form>

      {loading && <IsLoading />}

      {error && (
        <div className="w-11/12 max-w-[520px] p-3 m-auto text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}

      {blockNumber && !loading && (
        <div className="w-full max-w-screen-xl px-4 mx-auto md:px-8">
          <div className="mb-3 text-sm text-gray-500">
            Snapshot block: <span className="font-semibold">{blockNumber}</span>
            {isTruncated && (
              <span className="ml-2 text-orange-600">
                Partial result: transfer history is over {PAGE_SIZE * MAX_PAGES} rows.
              </span>
            )}
          </div>

          <div className="overflow-x-auto border rounded-lg shadow-sm">
            <table className="w-full text-sm text-left table-auto">
              <thead className="font-medium text-gray-600 border-b bg-gray-50">
                <tr>
                  <th className="px-6 py-3">Ticker</th>
                  <th className="px-6 py-3">Amount</th>
                  <th className="px-6 py-3">Token</th>
                  <th className="px-6 py-3">Contract</th>
                </tr>
              </thead>
              <tbody className="text-gray-600 divide-y">
                {holdings.length === 0 && (
                  <tr>
                    <td className="px-6 py-4" colSpan="4">
                      No ERC20 holdings found on {selectedNetwork.label}.
                    </td>
                  </tr>
                )}
                {holdings.map((token) => (
                  <tr key={token.contractAddress}>
                    <td className="px-6 py-4 font-medium whitespace-nowrap">
                      {token.symbol}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{token.balance}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{token.name}</td>
                    <td className="px-6 py-4 font-mono text-xs whitespace-nowrap">
                      {token.contractAddress}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            ERC20 only. Native {selectedNetwork.native}, NFTs, LP positions and DeFi
            positions are not included.
          </p>
        </div>
      )}
    </div>
  );
}
